/**
 * DocumentsTable Component
 *
 * Data table displaying documents for a specific client.
 * Supports sortable columns and optional document unassignment.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  AlertCircle,
  FileText,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Document } from '@/types/management';

type SortColumn = 'title' | 'source' | 'sourceDate' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  direction: SortDirection;
}

interface DocumentsTableProps {
  documents: Document[];
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
  clientName: string;
  onUnassign?: (doc: Document) => Promise<void>;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCategory(category: string): string {
  const categoryNames: Record<string, string> = {
    meeting_transcript: 'Meeting',
    customer_conversation: 'Conversation',
    project_discussion: 'Discussion',
  };
  return categoryNames[category] || category;
}

function compareValues(
  a: string | null,
  b: string | null,
  direction: SortDirection,
  isDate: boolean,
): number {
  // Nulls always sort last regardless of direction
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  let result: number;
  if (isDate) {
    result = new Date(a).getTime() - new Date(b).getTime();
  } else {
    result = a.localeCompare(b, undefined, { sensitivity: 'base' });
  }

  return direction === 'asc' ? result : -result;
}

function SortIcon({
  column,
  sortState,
}: {
  column: SortColumn;
  sortState: SortState;
}) {
  if (sortState.column !== column) {
    return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground/50" />;
  }
  return sortState.direction === 'asc' ? (
    <ArrowUp className="ml-1 h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 h-3 w-3" />
  );
}

export function DocumentsTable({
  documents,
  isLoading,
  hasFetched,
  error,
  clientName,
  onUnassign,
}: DocumentsTableProps) {
  const [sortState, setSortState] = useState<SortState>({
    column: 'sourceDate',
    direction: 'desc',
  });
  const [unassignTarget, setUnassignTarget] = useState<Document | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [unassignError, setUnassignError] = useState<string | null>(null);

  const handleSort = useCallback((column: SortColumn) => {
    setSortState((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const sortedDocuments = useMemo(() => {
    const { column, direction } = sortState;
    return [...documents].sort((a, b) => {
      const isDate = column === 'sourceDate' || column === 'createdAt';
      return compareValues(a[column], b[column], direction, isDate);
    });
  }, [documents, sortState]);

  const handleUnassign = useCallback(async () => {
    if (!unassignTarget || !onUnassign) return;

    setProcessingId(unassignTarget.id);
    setUnassignError(null);
    try {
      await onUnassign(unassignTarget);
      setUnassignTarget(null);
    } catch (err) {
      setUnassignError(
        err instanceof Error ? err.message : 'Failed to remove document',
      );
    } finally {
      setProcessingId(null);
    }
  }, [unassignTarget, onUnassign]);

  const columnHeaders = (
    <TableRow>
      <TableHead
        className="cursor-pointer select-none"
        onClick={() => handleSort('title')}
      >
        <span className="inline-flex items-center">
          Document
          <SortIcon column="title" sortState={sortState} />
        </span>
      </TableHead>
      <TableHead
        className="cursor-pointer select-none"
        onClick={() => handleSort('source')}
      >
        <span className="inline-flex items-center">
          Source
          <SortIcon column="source" sortState={sortState} />
        </span>
      </TableHead>
      <TableHead
        className="cursor-pointer select-none"
        onClick={() => handleSort('sourceDate')}
      >
        <span className="inline-flex items-center">
          Source Date
          <SortIcon column="sourceDate" sortState={sortState} />
        </span>
      </TableHead>
      <TableHead
        className="cursor-pointer select-none"
        onClick={() => handleSort('createdAt')}
      >
        <span className="inline-flex items-center">
          Classified
          <SortIcon column="createdAt" sortState={sortState} />
        </span>
      </TableHead>
      <TableHead>Category</TableHead>
      {onUnassign && <TableHead className="w-[60px]" />}
    </TableRow>
  );

  // Show skeleton while loading OR before first successful fetch completes
  if (isLoading || (!hasFetched && documents.length === 0)) {
    return (
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Source Date</TableHead>
              <TableHead>Classified</TableHead>
              <TableHead>Category</TableHead>
              {onUnassign && <TableHead className="w-[60px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-48" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-32" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-24" />
                </TableCell>
                {onUnassign && (
                  <TableCell>
                    <Skeleton className="h-8 w-8" />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive p-4 border rounded-md">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  }

  // Empty state only after we've confirmed there are no documents (hasFetched is true)
  if (documents.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No documents yet"
        description={`No documents have been synced for ${clientName}. Run a sync to import documents.`}
      />
    );
  }

  return (
    <>
      <div className="border rounded-md">
        <Table>
          <TableHeader>{columnHeaders}</TableHeader>
          <TableBody>
            {sortedDocuments.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell className="max-w-[400px]">
                  {doc.documentUrl ? (
                    <a
                      href={doc.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium hover:underline block truncate"
                    >
                      {doc.title}
                    </a>
                  ) : (
                    <div className="font-medium truncate">{doc.title}</div>
                  )}
                </TableCell>
                <TableCell>
                  {doc.sourceUrl ? (
                    <a
                      href={doc.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Badge
                        variant="outline"
                        className="cursor-pointer hover:bg-accent gap-1"
                      >
                        {doc.source}
                        <ExternalLink className="h-3 w-3" />
                      </Badge>
                    </a>
                  ) : (
                    <Badge variant="outline">{doc.source}</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(doc.sourceDate)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(doc.createdAt)}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {formatCategory(doc.category)}
                  </Badge>
                </TableCell>
                {onUnassign && (
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setUnassignTarget(doc)}
                            disabled={processingId === doc.id}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Remove from client</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="text-sm text-muted-foreground mt-2">
        {documents.length} document{documents.length !== 1 ? 's' : ''}
      </div>

      {/* Unassign Confirmation Dialog */}
      <AlertDialog
        open={!!unassignTarget}
        onOpenChange={() => {
          setUnassignTarget(null);
          setUnassignError(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Document from Client</AlertDialogTitle>
            <AlertDialogDescription>
              This will move &quot;{unassignTarget?.title}&quot; to pending
              classification for reassignment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {unassignError && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{unassignError}</span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingId === unassignTarget?.id}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleUnassign}
              disabled={processingId === unassignTarget?.id}
            >
              {processingId === unassignTarget?.id ? 'Removing...' : 'Remove'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
